/**
 * Learning Intelligence System API Service
 *
 * Provides functions for AI-powered learning intelligence reports:
 * - Generate intelligence reports
 * - List and view reports
 * - Approve/reject/apply recommendations
 */

import { apiClient } from "./apiClient";
import { logger } from "./logger";

export interface IntelligenceRecommendation {
  id: string;
  report_id: string;
  recommendation_type: string;
  target_setting_key: string;
  target_label: string;
  section: string | null;
  subsection: string | null;
  current_value: string;
  recommended_value: string;
  value_type: string;
  priority: string;
  confidence: number;
  reason: string;
  impact_analysis: string | null;
  evidence: Record<string, unknown> | null;
  status: string;
  created_at: string;
  approved_at: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  applied_at: string | null;
}

export interface IntelligenceReportItem {
  id: string;
  report_type: string;
  status: string;
  title: string | null;
  summary: string | null;
  sections: Array<{ heading: string; content: string; type: string; data?: Record<string, unknown> }> | null;
  trade_count: number;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
  created_at: string;
  recommendations: IntelligenceRecommendation[];
}

export interface IntelligenceReportListResponse {
  reports: IntelligenceReportItem[];
  total: number;
}

export interface IntelligenceReportDetailResponse {
  id: string;
  report_type: string;
  parameters: Record<string, unknown> | null;
  status: string;
  title: string | null;
  summary: string | null;
  sections: Array<{ heading: string; content: string; type: string; data?: Record<string, unknown> }> | null;
  trade_count: number;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  recommendations: IntelligenceRecommendation[];
}

export interface ApproveRejectResponse {
  success: boolean;
  recommendation_id: string;
  status: string;
  message: string;
}

export const REPORT_TYPES = [
  { value: "general", label: "General Analysis", description: "Overall analysis of all trades" },
  { value: "winner_vs_loser", label: "Winners vs Losers", description: "Compare winning vs losing trades" },
  { value: "winner_vs_breakeven", label: "Winners vs Breakeven", description: "Compare winning vs breakeven trades" },
  { value: "loser_vs_breakeven", label: "Losers vs Breakeven", description: "Compare losing vs breakeven trades" },
  { value: "filter_effectiveness", label: "Filter Effectiveness", description: "Analyze which filters contribute most to success" },
  { value: "weight_recommendation", label: "Weight Recommendations", description: "Specific recommendations for weight adjustments" },
  { value: "custom", label: "Custom Analysis", description: "Use a custom prompt for analysis" },
] as const;

export async function generateIntelligenceReport(
  params: { report_type: string; days?: number; max_trades?: number; custom_prompt?: string }
): Promise<IntelligenceReportItem> {
  try {
    const response = await apiClient.post<IntelligenceReportItem>(
      "/admin/reports/learning-intelligence/generate",
      params,
      { timeout: 300000 },
    );
    return response.data;
  } catch (error: any) {
    logger.error("Error generating intelligence report:", error as Error);
    throw error;
  }
}

export async function listIntelligenceReports(
  params?: { limit?: number; offset?: number; report_type?: string }
): Promise<IntelligenceReportListResponse> {
  try {
    const response = await apiClient.get<IntelligenceReportListResponse>(
      "/admin/reports/learning-intelligence/reports",
      { params },
    );
    return response.data;
  } catch (error: any) {
    logger.error("Error listing intelligence reports:", error as Error);
    throw error;
  }
}

export async function getIntelligenceReportDetail(
  reportId: string
): Promise<IntelligenceReportDetailResponse> {
  try {
    const response = await apiClient.get<IntelligenceReportDetailResponse>(
      `/admin/reports/learning-intelligence/reports/${reportId}`,
    );
    return response.data;
  } catch (error: any) {
    logger.error("Error fetching intelligence report detail:", error as Error);
    throw error;
  }
}

export async function approveRecommendation(
  recommendationId: string
): Promise<ApproveRejectResponse> {
  try {
    const response = await apiClient.post<ApproveRejectResponse>(
      `/admin/reports/learning-intelligence/recommendations/${recommendationId}/approve`,
    );
    return response.data;
  } catch (error: any) {
    logger.error("Error approving recommendation:", error as Error);
    throw error;
  }
}

export async function rejectRecommendation(
  recommendationId: string,
  reason?: string
): Promise<ApproveRejectResponse> {
  try {
    const response = await apiClient.post<ApproveRejectResponse>(
      `/admin/reports/learning-intelligence/recommendations/${recommendationId}/reject`,
      null,
      { params: { rejection_reason: reason } },
    );
    return response.data;
  } catch (error: any) {
    logger.error("Error rejecting recommendation:", error as Error);
    throw error;
  }
}

export async function applyRecommendation(
  recommendationId: string
): Promise<ApproveRejectResponse> {
  try {
    const response = await apiClient.post<ApproveRejectResponse>(
      `/admin/reports/learning-intelligence/recommendations/${recommendationId}/apply`,
    );
    return response.data;
  } catch (error: any) {
    logger.error("Error applying recommendation:", error as Error);
    throw error;
  }
}
